import React, {
  lazy,
  Suspense,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@resy/ui-kitchen';
import { isDevicePixelRatio2x, validateImageFileFormat } from '../../AmexMarketingUtils/AmexUtils';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexCenturionBanner.scss';

const AmexCenturionBanner = (
  {
    awsFolderName = '',
    bannerConfig = {},
    baseRequestUrl,
    Analytics,
    matchMedia,
  }
) => {
  const {
    show_video: showVideo,
    heading,
    description,
    centurion_logo: logo,
    images: {
      desktop: desktopImg = {},
      mobile: mobileImg = {},
    } = {},
    video = {},
  } = bannerConfig;

  if (Object.keys(bannerConfig).length === 0) return null;
  const assetsPath = `${baseRequestUrl}${awsFolderName}/banner/`;
  const imageType = matchMedia.lg ? desktopImg : mobileImg;
  const imageName = isDevicePixelRatio2x() ? imageType?.retina : imageType?.regular;
  const image = validateImageFileFormat(imageName);
  const [videoHasError, setVideoHasError] = useState(false);
  const displayVideo = Object.keys(video).length > 0 && showVideo && !videoHasError;
  const AmexVideo = useMemo(() => lazy(() => import('../../AmexMarketingUtils/AmexVideo')), []);
  const SuspenseWrapper = (page) => (<Suspense fallback={<Loader />}>{page}</Suspense>);

  const handleVideoError = (value) => {
    setVideoHasError(value);
  };

  const renderStaticImage = () => {
    if (!image) return null;
    return (
      <img
        src={`${assetsPath}${image}`}
        className="AmexCenturionBanner__hero--img"
        alt=""
      />
    );
  };

  const renderVideo = () => (
    <div className="AmexCenturionBanner__video-container">
      {SuspenseWrapper(
        <AmexVideo
          baseRequestUrl={baseRequestUrl}
          Analytics={Analytics}
          matchMedia={matchMedia}
          video={video}
          videoPath={assetsPath}
          handleVideoError={handleVideoError}
        />
      )}
    </div>
  );

  const sanitizedHeading = sanitizeString(heading);
  const sanitizedDescription = sanitizeString(description);

  return (
    <header className="AmexCenturionBanner">
      <div className="AmexCenturionBanner__hero">
        <img
          src={`${baseRequestUrl}${awsFolderName}/logos/${logo}`}
          className="AmexCenturionBanner__hero--logo"
          alt="Centurion New York logo"
        />
        {displayVideo ? renderVideo() : renderStaticImage()}
      </div>
      <div className="AmexCenturionBanner__intro">
        <div className="container">
          {heading && (
            <h1
              className="AmexCenturionBanner__intro--title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedHeading,
              }}
            />
          )}
          {description && (
            <p
              className="AmexCenturionBanner__intro--text"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedDescription,
              }}
            />
          )}
        </div>
      </div>
    </header>
  );
};

AmexCenturionBanner.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  bannerConfig: PropTypes.shape({
    centurion_logo: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    images: PropTypes.shape({
      desktop: PropTypes.shape({}),
      mobile: PropTypes.shape({}),
    }).isRequired,
    video: PropTypes.shape({}).isRequired,
  }).isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
};

export default AmexCenturionBanner;
